import { easeCubicIn, easeCubicOut } from "d3";
import type { Selection } from "d3";

export type AppletElement = HTMLElement & {
  dataset: DOMStringMap & {
    graphId?: string;
    growthMax?: string;
    growthStep?: string;
    pathMax?: string;
    pathStep?: string;
    rendered?: string;
  };
};

export type SvgSelection = Selection<SVGSVGElement, unknown, null, undefined>;
export type GroupSelection = Selection<SVGGElement, unknown, null, undefined>;

export type EdgeEndpoints = Readonly<{
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}>;

export type Point = Readonly<{
  x: number;
  y: number;
}>;

export type StepControlsConfig = Readonly<{
  element: AppletElement;
  frameCount: number;
  onFrameChange: (frameIndex: number) => void;
}>;

export type AnimatedGrowthLabel = Readonly<{
  id: string;
  label: string;
  x: number;
  y: number;
  fontSize: number;
  fill: string;
}>;

export type CaterpillarNodeKind = "leg" | "spine";

export type CaterpillarDiagramNode = Readonly<{
  id: string;
  x: number;
  y: number;
  kind: CaterpillarNodeKind;
}>;

export type CaterpillarDiagramEdge = Readonly<{
  source: string;
  target: string;
}>;

export type CaterpillarLegendItem = Readonly<{
  kind: CaterpillarNodeKind;
  label: string;
  x: number;
}>;

export const NODE_STROKE = "black";
export const EDGE_STROKE = "black";
export const GROWTH_BACKGROUND_NODE_FILL = "#e9f1e6";
export const GROWTH_BACKGROUND_NODE_STROKE = "#adbcaa";
export const GROWTH_BACKGROUND_EDGE_STROKE = "#a5b19f";
export const GROWTH_BACKGROUND_OPACITY = 0.6;
export const CATERPILLAR_DIAGRAM_WIDTH = 620;
export const CATERPILLAR_DIAGRAM_HEIGHT = 230;
export const CATERPILLAR_SPINE_NODE_RADIUS = 9;
export const CATERPILLAR_LEG_NODE_RADIUS = 7;
export const CATERPILLAR_SPINE_FILL = "#8fbc8f";
export const CATERPILLAR_LEG_FILL = "#c9ddc5";
export const CATERPILLAR_NODE_STROKE = "#3f5f3f";
export const CATERPILLAR_LEG_STROKE_WIDTH = 1.2;
export const CATERPILLAR_SPINE_STROKE_WIDTH = 2.4;
export const CATERPILLAR_EDGE_STROKE = "#444";
export const CATERPILLAR_LEGEND_Y = 207;

const GROWTH_LABEL_MIN_SCALE = 0.01;
const GROWTH_LABEL_COLLAPSE_DURATION_MS = 270;
const GROWTH_LABEL_GROW_DURATION_MS = 390;

export const CATERPILLAR_STRUCTURE_NODES: readonly CaterpillarDiagramNode[] = [
  { id: "s0", x: 56, y: 126, kind: "spine" },
  { id: "s1", x: 132, y: 98, kind: "spine" },
  { id: "s2", x: 208, y: 120, kind: "spine" },
  { id: "s3", x: 284, y: 94, kind: "spine" },
  { id: "s4", x: 360, y: 122, kind: "spine" },
  { id: "s5", x: 436, y: 100, kind: "spine" },
  { id: "s6", x: 512, y: 126, kind: "spine" },
  { id: "l1a", x: 114, y: 48, kind: "leg" },
  { id: "l1b", x: 154, y: 150, kind: "leg" },
  { id: "l2a", x: 210, y: 164, kind: "leg" },
  { id: "l3a", x: 262, y: 40, kind: "leg" },
  { id: "l3b", x: 306, y: 42, kind: "leg" },
  { id: "l3c", x: 286, y: 154, kind: "leg" },
  { id: "l5a", x: 418, y: 46, kind: "leg" },
  { id: "l5b", x: 458, y: 150, kind: "leg" },
  { id: "l6a", x: 556, y: 94, kind: "leg" },
];

export const CATERPILLAR_STRUCTURE_EDGES: readonly CaterpillarDiagramEdge[] = [
  { source: "s0", target: "s1" },
  { source: "s1", target: "s2" },
  { source: "s2", target: "s3" },
  { source: "s3", target: "s4" },
  { source: "s4", target: "s5" },
  { source: "s5", target: "s6" },
  { source: "s1", target: "l1a" },
  { source: "s1", target: "l1b" },
  { source: "s2", target: "l2a" },
  { source: "s3", target: "l3a" },
  { source: "s3", target: "l3b" },
  { source: "s3", target: "l3c" },
  { source: "s5", target: "l5a" },
  { source: "s5", target: "l5b" },
  { source: "s6", target: "l6a" },
];

export const CATERPILLAR_SPINE_IDS: readonly string[] = ["s0", "s1", "s2", "s3", "s4", "s5", "s6"];

export const CATERPILLAR_LEGS_BY_SPINE: Readonly<Record<string, readonly string[]>> = {
  s0: [],
  s1: ["l1a", "l1b"],
  s2: ["l2a"],
  s3: ["l3a", "l3b", "l3c"],
  s4: [],
  s5: ["l5a", "l5b"],
  s6: ["l6a"],
};

const CATERPILLAR_LEGEND_ITEMS: readonly CaterpillarLegendItem[] = [
  { kind: "spine", label: "spine", x: 250 },
  { kind: "leg", label: "leg", x: 365 },
];

export function renderApplet(selector: string, render: (element: AppletElement) => void): void {
  const renderAll = (): void => {
    document.querySelectorAll<AppletElement>(selector).forEach((element: AppletElement): void => {
      if (element.dataset.rendered === "true") {
        return;
      }

      render(element);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", renderAll);
  } else {
    renderAll();
  }
}

export function trimmedEdgeEndpoints(
  source: Point,
  target: Point,
  sourceRadius: number,
  targetRadius = sourceRadius,
): EdgeEndpoints {
  const dx = target.x - source.x;
  const dy = target.y - source.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) {
    return { x1: source.x, y1: source.y, x2: target.x, y2: target.y };
  }

  const sourceOffsetX = (dx / length) * sourceRadius;
  const sourceOffsetY = (dy / length) * sourceRadius;
  const targetOffsetX = (dx / length) * targetRadius;
  const targetOffsetY = (dy / length) * targetRadius;
  return {
    x1: source.x + sourceOffsetX,
    y1: source.y + sourceOffsetY,
    x2: target.x - targetOffsetX,
    y2: target.y - targetOffsetY,
  };
}

export function appendStepControls(config: StepControlsConfig): void {
  let frameIndex = 0;
  const lastFrameIndex = config.frameCount - 1;
  const controls = document.createElement("div");
  controls.className = "applet-step-controls";

  const previousButton = document.createElement("button");
  previousButton.type = "button";
  previousButton.className = "applet-step-previous";
  previousButton.ariaLabel = "Step backward";
  previousButton.textContent = "<";

  const frameStatus = document.createElement("output");
  frameStatus.className = "applet-step-status";
  frameStatus.ariaLive = "polite";

  const nextButton = document.createElement("button");
  nextButton.type = "button";
  nextButton.className = "applet-step-next";
  nextButton.ariaLabel = "Step forward";
  nextButton.textContent = ">";

  const setFrame = (nextFrameIndex: number): void => {
    frameIndex = Math.max(0, Math.min(lastFrameIndex, nextFrameIndex));
    config.onFrameChange(frameIndex);
    frameStatus.value = `${frameIndex} / ${lastFrameIndex}`;
    frameStatus.textContent = frameStatus.value;
    previousButton.disabled = frameIndex === 0;
    nextButton.disabled = frameIndex === lastFrameIndex;
  };

  previousButton.addEventListener("click", (): void => setFrame(frameIndex - 1));
  nextButton.addEventListener("click", (): void => setFrame(frameIndex + 1));

  controls.append(previousButton, frameStatus, nextButton);
  config.element.append(controls);
  setFrame(0);
}

export function renderAnimatedGrowthLabels(
  graph: GroupSelection,
  layerClass: string,
  currentLabels: readonly AnimatedGrowthLabel[],
  previousLabels?: readonly AnimatedGrowthLabel[],
): void {
  const layer = graph.append<SVGGElement>("g")
    .attr("class", layerClass)
    .attr("pointer-events", "none");

  if (previousLabels === undefined) {
    currentLabels.forEach((label: AnimatedGrowthLabel): void => {
      appendGrowthLabel(layer, label, label.label, 1);
    });
    return;
  }

  const previousLabelsById = new Map(previousLabels.map((label: AnimatedGrowthLabel): [string, AnimatedGrowthLabel] => [label.id, label]));
  const currentLabelIds = new Set(currentLabels.map((label: AnimatedGrowthLabel): string => label.id));

  currentLabels.forEach((label: AnimatedGrowthLabel): void => {
    const previousLabel = previousLabelsById.get(label.id);
    if (previousLabel === undefined) {
      growGrowthLabel(layer, label, 0);
      return;
    }

    if (previousLabel.label === label.label) {
      appendGrowthLabel(layer, label, label.label, 1);
      return;
    }

    collapseGrowthLabel(layer, previousLabel);
    growGrowthLabel(layer, label, GROWTH_LABEL_COLLAPSE_DURATION_MS);
  });

  previousLabels.forEach((label: AnimatedGrowthLabel): void => {
    if (!currentLabelIds.has(label.id)) {
      collapseGrowthLabel(layer, label);
    }
  });
}

function growthLabelTransform(label: AnimatedGrowthLabel, scale: number): string {
  return `translate(${label.x},${label.y}) scale(${scale})`;
}

function appendGrowthLabel(
  layer: GroupSelection,
  label: AnimatedGrowthLabel,
  text: string,
  scale: number,
): GroupSelection {
  const labelGroup = layer.append<SVGGElement>("g")
    .attr("class", "growth-label")
    .attr("transform", growthLabelTransform(label, scale));

  labelGroup.append<SVGTextElement>("text")
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "central")
    .attr("font-family", "Avenir Next, Helvetica, Arial, sans-serif")
    .attr("font-size", label.fontSize)
    .attr("font-weight", 500)
    .attr("fill", label.fill)
    .text(text);

  return labelGroup;
}

function collapseGrowthLabel(layer: GroupSelection, label: AnimatedGrowthLabel): void {
  appendGrowthLabel(layer, label, label.label, 1)
    .transition()
    .duration(GROWTH_LABEL_COLLAPSE_DURATION_MS)
    .ease(easeCubicIn)
    .attr("transform", growthLabelTransform(label, GROWTH_LABEL_MIN_SCALE))
    .remove();
}

function growGrowthLabel(layer: GroupSelection, label: AnimatedGrowthLabel, delayMs: number): void {
  appendGrowthLabel(layer, label, label.label, GROWTH_LABEL_MIN_SCALE)
    .transition()
    .delay(delayMs)
    .duration(GROWTH_LABEL_GROW_DURATION_MS)
    .ease(easeCubicOut)
    .attr("transform", growthLabelTransform(label, 1));
}

export function caterpillarNodeLookup(): ReadonlyMap<string, CaterpillarDiagramNode> {
  return new Map(CATERPILLAR_STRUCTURE_NODES.map((node) => [node.id, node]));
}

export function caterpillarNodeFill(kind: CaterpillarNodeKind): string {
  return kind === "leg" ? CATERPILLAR_LEG_FILL : CATERPILLAR_SPINE_FILL;
}

export function caterpillarNodeRadius(kind: CaterpillarNodeKind): number {
  return kind === "leg" ? CATERPILLAR_LEG_NODE_RADIUS : CATERPILLAR_SPINE_NODE_RADIUS;
}

export function caterpillarNodeStrokeWidth(kind: CaterpillarNodeKind): number {
  return kind === "leg" ? CATERPILLAR_LEG_STROKE_WIDTH : CATERPILLAR_SPINE_STROKE_WIDTH;
}

export function caterpillarEdgeEndpoints(
  edge: CaterpillarDiagramEdge,
  nodes: ReadonlyMap<string, CaterpillarDiagramNode>,
): EdgeEndpoints {
  const source = nodes.get(edge.source);
  const target = nodes.get(edge.target);
  if (source === undefined || target === undefined) {
    return { x1: 0, y1: 0, x2: 0, y2: 0 };
  }

  return trimmedEdgeEndpoints(source, target, caterpillarNodeRadius(source.kind), caterpillarNodeRadius(target.kind));
}

export function renderCaterpillarLegend(svg: SvgSelection): void {
  const legend = svg.append<SVGGElement>("g")
    .attr("class", "caterpillar-structure-legend");

  const legendItems = legend.selectAll<SVGGElement, CaterpillarLegendItem>("g")
    .data(CATERPILLAR_LEGEND_ITEMS)
    .join("g")
    .attr("transform", (item: CaterpillarLegendItem): string => `translate(${item.x},${CATERPILLAR_LEGEND_Y})`);

  legendItems.append<SVGCircleElement>("circle")
    .attr("r", (item: CaterpillarLegendItem): number => caterpillarNodeRadius(item.kind) * 0.68)
    .attr("fill", (item: CaterpillarLegendItem): string => caterpillarNodeFill(item.kind))
    .attr("stroke", CATERPILLAR_NODE_STROKE)
    .attr("stroke-width", (item: CaterpillarLegendItem): number => caterpillarNodeStrokeWidth(item.kind) * 0.72);

  legendItems.append<SVGTextElement>("text")
    .attr("x", 12)
    .attr("y", 1)
    .attr("dominant-baseline", "middle")
    .attr("font-family", "Avenir Next, Helvetica, Arial, sans-serif")
    .attr("font-size", 12)
    .attr("fill", "#555")
    .text((item: CaterpillarLegendItem): string => item.label);
}
